import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import {
  nextStep,
  prevStep,
  addStepsData,
} from "app/lib/store/slices/steps/jointpersonalDetails";
import { usePersonalDetails } from "app/hooks/applicant/usePersonalDetails";
import { RootState } from "app/lib/store/store";
import { JointPersonalFormFourData } from "app/utils/types";
import MainTitle from "app/components/common/typography/MainTitle";
import ContinueButton from "app/components/common/buttons/Continue";
import PreviousButtom from "app/components/common/buttons/Previous";
import ProgressBar from "app/components/common/progressbar/ProgressBar";
import InfoTitle from "app/components/common/typography/InfoTitle";
import { APPLICANT } from "app/constants/apiEndPoints";
import toast from "react-hot-toast";
import DynamicFormFour from "app/components/Individual/personalDetails/dynamicForms/DynamicFormFour";

const PartFour: React.FC = () => {
  const dispatch = useDispatch();

  const savedIds = useSelector(
    (state: RootState) => state.loanTypesSteps.savedIds
  );
  const { mutateAsync: submitPersonalDetails, isPending } =
    usePersonalDetails();
  const { formFour, ...others } = useSelector(
    (state: RootState) => state.jointpersonalDetails.stepsData
  );
  const applicant = useSelector(
    (state: RootState) => state.loanTypesSteps.jointApplication
  );
  const { loanApplication } = useSelector(
    (state: RootState) => state.loanTypesSteps.stepsData
  );

  const isSpouse = loanApplication?.coApplicantRelationship === "spouse";

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    setError,
    clearErrors,
    watch,
  } = useForm<JointPersonalFormFourData>({
    defaultValues: {
      yourDetails: {
        numberOfDependents: 0,
      },
      coBorrowerDetails: { numberOfDependents: 0 },
    },
  });

  const yourNumberOfDependents = watch("yourDetails.numberOfDependents");
  const coNumberOfDependents = watch("coBorrowerDetails.numberOfDependents");
  const yourvisaStatus = watch("yourDetails.visaStatus");
  const covisaStatus = watch("coBorrowerDetails.visaStatus");

  const onSubmit = async (data: JointPersonalFormFourData) => {
    console.log("🚀 ~ onSubmit ~ data:", data);
    const { yourDetails, coBorrowerDetails } = data || {};
    const { dependentAges: yourDependentAges, ...rest } = yourDetails || {};
    const { dependentAges: coDependentAges, ...coBorrowerDetailsRest } =
      coBorrowerDetails || {};

    const yourDependentAgesData = (yourDependentAges || [])
      .filter((age) => age !== "" && age !== undefined)
      .join(",");
    const coDependentAgesData = (coDependentAges || [])
      .filter((age) => age !== "" && age !== undefined)
      .join(",");

    const yourDetailsData = {
      ...rest,
      dependentAges:
        Number(yourDetails.numberOfDependents) === 0
          ? "0"
          : yourDependentAgesData,
      numberOfDependents: Number(yourDetails.numberOfDependents),
    };

    const yourSavedData = {
      dependentAges: yourDependentAges,
      ...rest,
    };
    const coSavedData = {
      dependentAges: coDependentAges,
      ...coBorrowerDetailsRest,
    };

    const coBorrowerDetailsData = {
      ...coBorrowerDetailsRest,
      dependentAges: isSpouse
        ? Number(yourDetails.numberOfDependents) === 0
          ? "0"
          : yourDependentAgesData
        : Number(coBorrowerDetails.numberOfDependents) === 0
        ? "0"
        : coDependentAgesData,
      numberOfDependents: Number(coBorrowerDetails.numberOfDependents),
    };

    const request1 = {
      url: `${APPLICANT}/${savedIds?.applicantID}/fourth`,
      data: yourDetailsData,
    };
    const request2 = {
      url: `${APPLICANT}/${savedIds?.secondApplicantID}/fourth`,
      data: coBorrowerDetailsData,
    };
    const storedData = {
      yourDetails: yourSavedData,
      coBorrowerDetails: coSavedData,
    };

    try {
      const [response1, response2] = await Promise.all([
        submitPersonalDetails(request1),
        submitPersonalDetails(request2),
      ]);
      if (response1?.success && response2?.success) {
        dispatch(nextStep());
        dispatch(
          addStepsData({
            ...others,
            formFour: storedData,
          })
        );
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Something went wrong.");
    }
  };

  useEffect(() => {
    if (formFour) {
      reset(formFour);
    } else {
      const { YourDetails, Coborrower } = applicant || {};
      if (YourDetails?.maritalStatus) {
        setValue("yourDetails.maritalStatus", YourDetails.maritalStatus);
      }
      if (YourDetails?.address?.street) {
        setValue("yourDetails.address.street", YourDetails?.address?.street);
      }
      if (YourDetails?.address?.state) {
        setValue("yourDetails.address.state", YourDetails?.address?.state);
      }
      if (YourDetails?.numberOfDependents) {
        setValue(
          "yourDetails.numberOfDependents",
          YourDetails.numberOfDependents
        );
      }
      if (YourDetails?.address?.postCode) {
        setValue(
          "yourDetails.address.postCode",
          YourDetails?.address?.postCode
        );
      }

      if (Coborrower?.maritalStatus) {
        setValue("coBorrowerDetails.maritalStatus", Coborrower.maritalStatus);
      }
      if (Coborrower?.numberOfDependents) {
        setValue(
          "coBorrowerDetails.numberOfDependents",
          Coborrower.numberOfDependents
        );
      }

      if (Coborrower?.address?.street) {
        setValue(
          "coBorrowerDetails.address.street",
          Coborrower?.address?.street
        );
      }
      if (Coborrower?.address?.state) {
        setValue("coBorrowerDetails.address.state", Coborrower?.address?.state);
      }
      if (Coborrower?.address?.postCode) {
        setValue(
          "coBorrowerDetails.address.postCode",
          Coborrower?.address?.postCode
        );
      }
    }
  }, [formFour, reset]);

  useEffect(() => {
    if (yourNumberOfDependents === 0) {
      clearErrors("yourDetails.dependentAges");
    }
  }, [yourNumberOfDependents]);
  useEffect(() => {
    if (coNumberOfDependents === 0) {
      clearErrors("coBorrowerDetails.dependentAges");
    }
  }, [coNumberOfDependents]);

  return (
    <>
      <ProgressBar progress={75} MB={4} />
      <div className="w-full sm:w-[60%] lg:w-[40%] mx-auto text-center px-4 sm:px-0">
        <MainTitle title="Personal Details" />
      </div>
      <div className="mx-auto w-internalWidth">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="flex mb-5 flex-col md:flex-col xl:flex-row sm:gap-4 ">
            <div className="flex-1">
              <InfoTitle title="Your Details" />

              <DynamicFormFour
                control={control}
                errors={errors}
                register={register}
                setValue={setValue}
                setError={setError}
                clearErrors={clearErrors}
                visaStatus="yourDetails.visaStatus"
                maritalStatus="yourDetails.maritalStatus"
                visaStatusValue={yourvisaStatus}
                visaNumber="yourDetails.visaNumber"
                numberOfDependents={yourNumberOfDependents}
                numberOfDependentsName="yourDetails.numberOfDependents"
                dependentAges="yourDetails.dependentAges"
                addressStreetName="yourDetails.address.street"
                addressStateName="yourDetails.address.state"
                addressPostCodeName="yourDetails.address.postCode"
              />
            </div>
            <div className="flex-1">
              <InfoTitle title="Co-borrower" />

              <DynamicFormFour
                control={control}
                errors={errors}
                register={register}
                setValue={setValue}
                setError={setError}
                clearErrors={clearErrors}
                visaStatus="coBorrowerDetails.visaStatus"
                maritalStatus="coBorrowerDetails.maritalStatus"
                visaStatusValue={covisaStatus}
                visaNumber="coBorrowerDetails.visaNumber"
                numberOfDependents={coNumberOfDependents}
                numberOfDependentsName="coBorrowerDetails.numberOfDependents"
                dependentAges="coBorrowerDetails.dependentAges"
                addressStreetName="coBorrowerDetails.address.street"
                addressStateName="coBorrowerDetails.address.state"
                addressPostCodeName="coBorrowerDetails.address.postCode"
                disabledOnisSpouse={isSpouse}
              />
            </div>
          </div>

          <div className="flex justify-between mb-6">
            <PreviousButtom
              onClick={() => {
                dispatch(prevStep());
              }}
            >
              Previous
            </PreviousButtom>
            <ContinueButton type="submit" loading={isPending}>
              Next
            </ContinueButton>
          </div>
        </form>
      </div>
    </>
  );
};

export default PartFour;
